import type { SDKStorage } from '../storage/types.js';

/**
 * Durable mirror for the client caches (sdk ADR-92811): the storage seam
 * under the dedicated `la:cache:` namespace, never the credentials namespace.
 * Each cache owns one sub-namespace (`la:cache:<owner>`) and hydrates and
 * clears only that.
 *
 * Synchronous (the seam is sync by contract): write-through on mutation,
 * hydrate-once on first use. A blob written by a different schema version or
 * unparseable is REMOVED and treated as absent — a cache snapshot is a paint
 * skeleton, never trusted state (no-fallbacks.md: the degradation is the
 * designed cache-miss refetch, nothing is silently served).
 */
export const CACHE_NAMESPACE = 'la:cache:';
export const CACHE_SNAPSHOT_VERSION = 1;

export interface DurableCacheEntry {
  version: number;
  value: unknown;
}

export class DurableCache<K> {
  private hydrated = false;
  private readonly prefix: string;

  constructor(
    private readonly storage: SDKStorage | null,
    /** The owner's sub-namespace, e.g. `history:`; every durable key is `la:cache:<owner><cache key>`. */
    owner: string,
    /** Re-insert a hydrated entry into the in-memory store, by its cache key. */
    private readonly restore: (key: string, value: K) => void,
    /** Enumerate the in-memory keys (for write-through + eviction). */
    private readonly inMemoryKeys: () => string[],
    private readonly inMemoryGet: (key: string) => K | undefined,
    private readonly inMemoryDelete: (key: string) => void,
  ) {
    this.prefix = `${CACHE_NAMESPACE}${owner}`;
  }

  /** Load this owner's stored snapshots into memory. Once; sync by the seam's contract. */
  hydrate(): void {
    if (this.hydrated || !this.storage) return;
    this.hydrated = true;
    for (const fullKey of this.storage.keys()) {
      if (!fullKey.startsWith(this.prefix)) continue;
      const raw = this.storage.getItem(fullKey);
      if (raw === null) continue;
      try {
        const parsed = JSON.parse(raw) as DurableCacheEntry;
        if (parsed.version !== CACHE_SNAPSHOT_VERSION) {
          this.storage.removeItem(fullKey);
          continue;
        }
        this.restore(fullKey.slice(this.prefix.length), parsed.value as K);
      } catch {
        this.storage.removeItem(fullKey);
      }
    }
  }

  writeThrough(key: string, value: K): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(
        this.prefix + key,
        JSON.stringify({ version: CACHE_SNAPSHOT_VERSION, value } satisfies DurableCacheEntry),
      );
    } catch {
      // no-fallbacks.md exception: quota/private-mode failures degrade the
      // SNAPSHOT only — the in-memory cache is untouched and remains correct.
    }
  }

  remove(key: string): void {
    if (!this.storage) return;
    this.storage.removeItem(this.prefix + key);
  }

  /** Drop this owner's durable entries whose cache key starts with `keyPrefix`. */
  evictDurablePrefix(keyPrefix: string): void {
    if (!this.storage) return;
    for (const fullKey of this.storage.keys()) {
      if (fullKey.startsWith(this.prefix + keyPrefix)) this.storage.removeItem(fullKey);
    }
  }

  /** Drop every durable entry this owner wrote, and no other owner's. */
  evictAllDurable(): void {
    if (!this.storage) return;
    for (const fullKey of this.storage.keys()) {
      if (fullKey.startsWith(this.prefix)) this.storage.removeItem(fullKey);
    }
  }

  /** Drop both the in-memory entry and its durable mirror. */
  evictEntry(key: string): void {
    this.inMemoryDelete(key);
    this.remove(key);
  }

  /** Enumerate every entry (in-memory keys are the source of truth). */
  entries(): Array<{ key: string; value: K | undefined }> {
    this.hydrate();
    return this.inMemoryKeys().map((key) => ({ key, value: this.inMemoryGet(key) }));
  }
}
